import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type {
  ClassFeeDueList,
  ClassFeeReminderResult,
  FeeAccountListParams,
  FeeAccountRow,
  FeeDayBookEntry,
  FeeDayBookParams,
  FeeDueWhen,
  FeeLedgerTotals,
  FeePaymentMethod,
  FeeReceipt,
  FeeReminderResult,
  FeeStatus,
  SendClassFeeRemindersPayload,
  SendFeeRemindersPayload,
  StudentFeeAccount,
  ClassInstallments,
} from '@/types/fees';
import type { FeeChargePayload, PaymentPayload } from '../schemas/fee.schema';

/**
 * Every call the fee counter makes.
 *
 * As with the rest of the institute area there is no institute id in any path —
 * the server takes it from the caller's token, so there is nothing for a client
 * to point at another institute's ledger. The academic year is the server's
 * decision too: the register and the day book are always this session's.
 */

/** Blank filters are dropped so the URL stays readable and `status=` is not a status. */
function usedOnly<T extends object>(params: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value !== undefined),
  );
}

export interface FeeAccountListResult {
  items: FeeAccountRow[];
  /**
   * How the register breaks down, counted over the *same* filters as the page —
   * so the summary always describes what is on screen rather than quoting a
   * whole-institute total beside three filtered rows.
   */
  byStatus: Record<FeeStatus, number>;
  /**
   * What is still outstanding under each standing, as decimal strings.
   *
   * Beside the counts because they answer different questions: fourteen unpaid
   * accounts could be fourteen thousand rupees or fourteen lakh, and only the
   * second is worth an afternoon of phone calls.
   */
  balanceByStatus: Record<FeeStatus, string>;
  totals: FeeLedgerTotals;
  academicYear: { uuid: string; name: string };
  meta: PaginationMeta;
}

export interface FeeDayBookResult {
  items: FeeDayBookEntry[];
  /** What the institute holds, and what came back off it. An office needs both. */
  totals: { collected: string; cancelled: string };
  /** So much cash, so much UPI — what a counter is balanced against. */
  byMethod: { method: FeePaymentMethod; amount: string; count: number }[];
  academicYear: { uuid: string; name: string };
  meta: PaginationMeta;
}

const EMPTY_META: PaginationMeta = { page: 1, limit: 20, total: 0, totalPages: 1 };

export const feesApi = {
  listAccounts: async (params: FeeAccountListParams): Promise<FeeAccountListResult> => {
    const response = await apiClient.get<
      ApiSuccess<{
        items: FeeAccountRow[];
        byStatus: Record<FeeStatus, number>;
        balanceByStatus: Record<FeeStatus, string>;
        totals: FeeLedgerTotals;
        academicYear: { uuid: string; name: string };
      }>
    >('/institute/fees/accounts', { params: usedOnly(params) });

    const { items, byStatus, balanceByStatus, totals, academicYear } = response.data.data;
    return {
      items,
      byStatus,
      balanceByStatus,
      totals,
      academicYear,
      meta: response.data.meta ?? { ...EMPTY_META, total: items.length },
    };
  },

  listPayments: async (params: FeeDayBookParams): Promise<FeeDayBookResult> => {
    const response = await apiClient.get<
      ApiSuccess<{
        items: FeeDayBookEntry[];
        totals: { collected: string; cancelled: string };
        byMethod: { method: FeePaymentMethod; amount: string; count: number }[];
        academicYear: { uuid: string; name: string };
      }>
    >('/institute/fees/payments', { params: usedOnly(params) });

    const { items, totals, byMethod, academicYear } = response.data.data;
    return {
      items,
      totals,
      byMethod,
      academicYear,
      meta: response.data.meta ?? { ...EMPTY_META, total: items.length },
    };
  },

  /** Every class's installments this session. */
  listInstallments: () =>
    unwrap(apiClient.get<ApiSuccess<ClassInstallments[]>>('/institute/fees/installments')),

  /** Replaces one class's installments. The whole list; an empty one removes the schedule. */
  setInstallments: (
    classUuid: string,
    installments: { name: string; dueDate: string; amount: number }[],
  ) =>
    unwrap(
      apiClient.put<ApiSuccess<ClassInstallments>>(
        `/institute/fees/classes/${classUuid}/installments`,
        { installments },
      ),
    ),

  getAccount: (studentUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<StudentFeeAccount>>(`/institute/fees/students/${studentUuid}`),
    ),

  /**
   * What this student is charged for the session. A `PUT`: the form states what
   * the account should say, and saving it twice leaves the same account.
   */
  setCharge: (studentUuid: string, payload: FeeChargePayload) =>
    unwrap(
      apiClient.put<ApiSuccess<StudentFeeAccount>>(
        `/institute/fees/students/${studentUuid}`,
        payload,
      ),
    ),

  /** Takes money and issues its receipt. Returns the receipt and the account it left. */
  recordPayment: (studentUuid: string, payload: PaymentPayload) =>
    unwrap(
      apiClient.post<ApiSuccess<{ receipt: FeeReceipt; account: StudentFeeAccount }>>(
        `/institute/fees/students/${studentUuid}/payments`,
        payload,
      ),
    ),

  /** Cancels a receipt and takes the money back off the account. */
  cancelPayment: (paymentUuid: string, reason: string) =>
    unwrap(
      apiClient.post<ApiSuccess<StudentFeeAccount>>(
        `/institute/fees/payments/${paymentUuid}/cancel`,
        { reason },
      ),
    ),

  /**
   * Reminds the families who still owe something.
   *
   * No number is ever sent: the request names either "everybody who owes" or
   * students by their public id, and the server looks up the parent to write
   * to. A student who owes nothing is never reminded, whoever was named.
   */
  sendReminders: (payload: SendFeeRemindersPayload) =>
    unwrap(apiClient.post<ApiSuccess<FeeReminderResult>>('/institute/fees/reminders', payload)),

  /**
   * A class teacher's own section: who still owes, which installment, by when.
   * The server fences it to their sections and sends no amounts.
   */
  classDues: (filter: { dueWhen?: FeeDueWhen; dueWithinDays?: number }) =>
    unwrap(
      apiClient.get<ApiSuccess<ClassFeeDueList>>('/institute/fees/class-reminders', {
        params: filter,
      }),
    ),

  /** A class teacher reminding their own section's families. Same rules as the office's send. */
  sendClassReminders: (payload: SendClassFeeRemindersPayload) =>
    unwrap(apiClient.post<ApiSuccess<ClassFeeReminderResult>>('/institute/fees/class-reminders', payload)),
};
